// NSARA Stores V2 — onglet "Clients" du workspace boutique (section 14).
//
// AUDIT: la table `customers` ne porte que business_id (sql/0002) — il n'existe
// AUCUNE colonne shop_id. Les clients sont donc globaux à l'entreprise et il
// serait faux d'inventer une appartenance à une boutique (interdit par la
// section 30: pas de migration pour un "customer-shop ownership").
//
// Ce qui EST sûr et réel, en revanche, c'est l'activité: une vente
// (sales.shop_id + sales.customer_id) et une commande publique
// (orders.shop_id + customer_name/phone) sont, elles, bien rattachées à une
// boutique. Cet onglet affiche donc les clients réellement servis PAR CETTE
// boutique, dérivés de ses ventes et de ses commandes — avec le nombre de
// passages et le montant cumulé. Le reste du fichier client de l'entreprise
// reste accessible dans le module Clients global, ce qui est expliqué ici
// plutôt que masqué.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Search, Users } from 'lucide-react';
import { getOrders, getSales, getCustomers } from '../../services/apiClient';
import { formatMoneyShort, shopMonogram } from './storeUtils';

const normalizePhone = (value) => String(value || '').replace(/[\s.-]/g, '');

export default function StoreCustomersTab({ businessId, shopId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    if (!businessId || !shopId) return;
    setLoading(true);
    setError('');
    try {
      // Ventes et commandes sont filtrées par shopId côté serveur. La liste
      // clients est volontairement globale: elle ne sert qu'à résoudre les
      // noms/téléphones, jamais à prétendre qu'un client "appartient" à la
      // boutique.
      const [ordersRes, salesRes, customersRes] = await Promise.all([
        getOrders({ businessId, shopId, limit: 200 }),
        getSales({ businessId, shopId, limit: 200 }),
        getCustomers({ businessId, limit: 200 })
      ]);

      const customers = customersRes.customers || [];
      const byId = new Map(customers.map((customer) => [customer.id, customer]));
      const byPhone = new Map(customers.filter((c) => c.phone).map((c) => [normalizePhone(c.phone), c]));
      const aggregated = new Map();

      const touch = (key, name, phone, amount) => {
        const existing = aggregated.get(key) || { key, name, phone, visits: 0, total: 0 };
        existing.visits += 1;
        existing.total += Number(amount) || 0;
        if (!existing.name && name) existing.name = name;
        if (!existing.phone && phone) existing.phone = phone;
        aggregated.set(key, existing);
      };

      for (const sale of salesRes.sales || []) {
        if (sale.status !== 'COMPLETED') continue;
        const customer = byId.get(sale.customer_id);
        if (!customer) continue;
        touch(`id:${customer.id}`, customer.name, customer.phone, sale.total);
      }

      for (const order of ordersRes.orders || []) {
        if (order.status === 'CANCELLED') continue;
        const phone = normalizePhone(order.customer_phone);
        const known = order.customer_id ? byId.get(order.customer_id) : byPhone.get(phone);
        // Une commande publique dont le client n'a pas encore été converti en
        // fiche client reste un client réel de cette boutique: on l'agrège par
        // téléphone plutôt que de la perdre.
        touch(known ? `id:${known.id}` : `phone:${phone}`, known?.name || order.customer_name, known?.phone || order.customer_phone, order.subtotal_estimated);
      }

      setRows([...aggregated.values()].sort((a, b) => b.total - a.total || String(a.name).localeCompare(String(b.name), 'fr')));
    } catch (err) {
      setError(err?.message || 'Impossible de charger les clients de cette boutique.');
    } finally {
      setLoading(false);
    }
  }, [businessId, shopId]);

  // shopId dans les dépendances + état vidé: changer de boutique ne conserve
  // jamais les clients de la boutique précédente.
  useEffect(() => { setRows([]); setQuery(''); load(); }, [load]);

  const visible = useMemo(() => {
    const search = query.trim().toLowerCase();
    if (!search) return rows;
    return rows.filter((row) => String(row.name || '').toLowerCase().includes(search) || String(row.phone || '').includes(search));
  }, [rows, query]);

  return (
    <div className="stores-section">
      <div className="stores-section-head">
        <div className="stores-head-text">
          <h2 className="stores-section-title">Clients</h2>
          <p className="stores-section-sub">
            {!loading && `${visible.length} client${visible.length > 1 ? 's' : ''} · `}
            les clients servis par cette boutique, d’après ses ventes et ses commandes.
          </p>
        </div>
      </div>

      {error && <p className="stores-alert stores-alert--error" role="alert">{error}</p>}

      <p className="stores-alert stores-alert--info">
        Le fichier client est commun à toute votre entreprise : un client n’appartient pas à une seule boutique.
        Retrouvez l’ensemble de vos clients dans le module Clients.
      </p>

      {rows.length > 0 && (
        <div className="stores-search">
          <Search size={16} aria-hidden="true" className="stores-search-icon" />
          <input
            className="stores-input"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Rechercher un client"
            aria-label="Rechercher un client"
          />
        </div>
      )}

      {loading && (
        <div className="stores-customer-list" aria-busy="true" aria-label="Chargement des clients">
          {[0, 1, 2].map((key) => <div key={key} className="stores-skeleton-row" style={{ height: 62 }} />)}
        </div>
      )}

      {!loading && visible.length === 0 && (
        <div className="stores-empty">
          <span className="stores-empty-icon" aria-hidden="true"><Users size={22} /></span>
          <h3>{rows.length === 0 ? 'Aucun client pour le moment' : 'Aucun client ne correspond'}</h3>
          <p>
            {rows.length === 0
              ? 'Dès qu’une commande ou une vente est enregistrée dans cette boutique, son client apparaît ici.'
              : 'Essayez un autre nom ou numéro de téléphone.'}
          </p>
        </div>
      )}

      {!loading && visible.length > 0 && (
        <div className="stores-customer-list">
          {visible.map((row) => (
            <div className="stores-customer-row" key={row.key}>
              <span className="stores-customer-mark" aria-hidden="true">{shopMonogram(row.name)}</span>
              <div className="stores-customer-body">
                <p className="stores-customer-name">{row.name}</p>
                <p className="stores-customer-meta">
                  {row.phone ? `${row.phone} · ` : ''}{row.visits} {row.visits > 1 ? 'passages' : 'passage'} · {formatMoneyShort(row.total)}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
